import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getTokenHolders, getTokenTransfers, getTokenDexMetrics, getTokenTransferMetrics, getEthPrice, getExportTransfersUrl, getExportHoldersUrl, getTokenMetadata } from '../lib/api';
import { getTokenColor, truncateAddress, formatNumber } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { HoldersTable } from '../components/HoldersTable';
import { TransfersTable } from '../components/TransfersTable';
import { Pagination } from '../components/Pagination';
import { AddressBadge, TxHashBadge } from '../components/AddressBadge';
import { ArrowLeft, Copy, Check, Download, TrendingUp, ArrowUpDown, BarChart3, Info, AlertTriangle, ShieldCheck, Coins, Droplets, Hammer } from 'lucide-react';
import { toast } from 'sonner';
import { copyToClipboard } from '../lib/utils';
import { TokenStats } from '../components/TokenStats';

const TRACKED_TOKENS = {
  "0x9189d145d1d8e612c4c48bbd1af5a50cbee63d7d": { symbol: "KROWN", name: "Krown", decimals: 18, isNative: true },
  "0xa3d9cfa4220d6a5ba7b9067d33ebedbd2de6f1cf": { symbol: "USDC", name: "USD Coin", decimals: 6 },
  "0x59973ae98d340ca0577ed92502402e82e987ba2e": { symbol: "USDT", name: "Tether USD", decimals: 6 },
  "0x6f9ec4f83ef69b923fa1dc00189e591728df0ac0": { symbol: "ETH", name: "Ether Token", decimals: 18 },
};

const KROWN_ADDRESS = "0x9189d145d1d8e612c4c48bbd1af5a50cbee63d7d";
const LIMIT = 50;

export default function TokenDetail() {
  const { tokenAddress } = useParams();
  const normalizedAddress = tokenAddress?.toLowerCase();
  const isKrown = normalizedAddress === KROWN_ADDRESS;

  const [metadata, setMetadata] = useState(null);
  const tokenInfo = TRACKED_TOKENS[normalizedAddress] || {
    symbol: metadata?.symbol || '?',
    name: metadata?.name || '',
    decimals: metadata?.decimals || 18,
    isNative: metadata?.is_native,
  };
  const effectiveSymbol = tokenInfo.symbol || metadata?.symbol || '?';
  const effectiveName = tokenInfo.name || metadata?.name || '';
  const effectiveDecimals = tokenInfo.decimals ?? metadata?.decimals ?? 18;
  const colors = getTokenColor(effectiveSymbol);

  const [dexMetrics, setDexMetrics] = useState(null);
  const [transferMetrics, setTransferMetrics] = useState(null);
  const [ethPrice, setEthPrice] = useState(null);
  const [holders, setHolders] = useState({ holders: [], total: 0 });
  const [transfers, setTransfers] = useState({ transfers: [], total: 0 });
  const [loading, setLoading] = useState({ dex: true, transfer: true, holders: true, transfers: true });
  const [holdersOffset, setHoldersOffset] = useState(0);
  const [transfersOffset, setTransfersOffset] = useState(0);
  const [copied, setCopied] = useState(false);

  // Fetch KrownScan-style metadata (verified, total_supply, creation, pairs)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const m = await getTokenMetadata(normalizedAddress);
        if (alive) setMetadata(m);
      } catch (e) {
        // soft-fail: keep TRACKED_TOKENS fallback
      }
    })();
    return () => { alive = false; };
  }, [normalizedAddress]);

  // Helper to format price source for display
  const getPriceSourceLabel = (source) => {
    const labels = {
      'manual_env': 'Manual Override',
      'mongo_cache': 'Cached (CoinGecko)',
      'coingecko': 'CoinGecko Live',
      'mongo_last_known': 'Last Known Price',
      'missing': 'Unavailable',
      'not_applicable': 'N/A (Stablecoin)'
    };
    return labels[source] || source;
  };

  // Fetch DEX metrics and ETH price (only for KROWN)
  useEffect(() => {
    const fetchDexMetrics = async () => {
      if (!isKrown) {
        setLoading(prev => ({ ...prev, dex: false }));
        return;
      }
      try {
        const [data, priceData] = await Promise.all([
          getTokenDexMetrics(normalizedAddress),
          getEthPrice()
        ]);
        setDexMetrics(data);
        setEthPrice(priceData);
      } catch (error) {
        console.error('Failed to fetch DEX metrics:', error);
      } finally {
        setLoading(prev => ({ ...prev, dex: false }));
      }
    };
    fetchDexMetrics();
  }, [normalizedAddress, isKrown]);

  // Fetch Transfer metrics
  useEffect(() => {
    const fetchTransferMetrics = async () => {
      try {
        const data = await getTokenTransferMetrics(normalizedAddress);
        setTransferMetrics(data);
      } catch (error) {
        console.error('Failed to fetch transfer metrics:', error);
      } finally {
        setLoading(prev => ({ ...prev, transfer: false }));
      }
    };
    fetchTransferMetrics();
  }, [normalizedAddress]);

  useEffect(() => {
    const fetchHolders = async () => {
      setLoading(prev => ({ ...prev, holders: true }));
      try {
        const data = await getTokenHolders(normalizedAddress, LIMIT, holdersOffset);
        // Backend returns {holders, count} — normalize to {holders, total}
        setHolders({ holders: data.holders || [], total: data.total ?? data.count ?? 0 });
      } catch (error) {
        console.error('Failed to fetch holders:', error);
      } finally {
        setLoading(prev => ({ ...prev, holders: false }));
      }
    };
    fetchHolders();
  }, [normalizedAddress, holdersOffset]);

  useEffect(() => {
    const fetchTransfers = async () => {
      setLoading(prev => ({ ...prev, transfers: true }));
      try {
        const data = await getTokenTransfers(normalizedAddress, LIMIT, transfersOffset);
        setTransfers({ transfers: data.transfers || [], total: data.total ?? data.count ?? 0 });
      } catch (error) {
        console.error('Failed to fetch transfers:', error);
      } finally {
        setLoading(prev => ({ ...prev, transfers: false }));
      }
    };
    fetchTransfers();
  }, [normalizedAddress, transfersOffset]);

  const handleCopyAddress = async () => {
    try {
      await copyToClipboard(normalizedAddress);
      setCopied(true);
      toast.success('Address copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  if (!effectiveSymbol || (effectiveSymbol === '?' && !metadata)) {
    return (
      <div className="text-center py-20" data-testid="token-loading">
        <h2 className="text-2xl font-bold mb-2">Loading token…</h2>
      </div>
    );
  }

  // Check for unpriced swaps in pool data
  const poolsWithUnpriced = dexMetrics?.by_pool?.filter(p => p.unpriced_count > 0) || [];
  const hasUnpricedSwaps = poolsWithUnpriced.length > 0;

  return (
    <TooltipProvider>
    <div className="space-y-8 animate-fade-in" data-testid="token-detail-page">
      {/* Back Link */}
      <Link 
        to="/" 
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        data-testid="back-to-dashboard"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </Link>

      {/* Token Header — KrownScan style */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className={`w-16 h-16 rounded-xl ${colors.bg} ${colors.border} border-2 flex items-center justify-center`}>
          <span className={`font-bold text-2xl ${colors.text}`}>
            {effectiveSymbol.charAt(0)}
          </span>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-3xl font-bold" data-testid="token-symbol">{effectiveSymbol}</h1>
            {metadata?.verified && (
              <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-xs" data-testid="token-verified-badge">
                <ShieldCheck className="w-3 h-3 mr-1" /> Verified
              </Badge>
            )}
            {metadata?.is_native && (
              <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30 text-xs">
                NATIVE
              </Badge>
            )}
            {metadata && !metadata.verified && (
              <Badge className="bg-rose-500/15 text-rose-400 border border-rose-500/30 text-xs">
                Unverified
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">{effectiveName}</p>
          <div className="flex items-center gap-2 mt-2">
            <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
              {truncateAddress(normalizedAddress, 10, 8)}
            </code>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleCopyAddress}
              data-testid="copy-token-address"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Decimals</p>
          <p className="text-2xl font-bold">{effectiveDecimals}</p>
        </div>
      </div>

      {/* KrownScan-style metadata strip */}
      {metadata && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="token-meta-strip">
          <Card className="border-border/60">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Coins className="w-3.5 h-3.5 text-amber-500" /> Total Supply
              </div>
              <p className="text-lg font-mono font-semibold truncate" data-testid="token-total-supply">
                {metadata.is_native ? 'Native — N/A' :
                  metadata.total_supply?.formatted
                    ? `${formatNumber(parseFloat(metadata.total_supply.formatted).toFixed(2))} ${effectiveSymbol}`
                    : '—'}
              </p>
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Hammer className="w-3.5 h-3.5 text-violet-500" /> Created
              </div>
              <p className="text-sm font-mono font-semibold truncate" data-testid="token-creation">
                {metadata.creation?.block_time
                  ? new Date(metadata.creation.block_time).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                  : metadata.is_native ? 'Genesis' : '—'}
              </p>
              {metadata.creation?.block_number !== undefined && (
                <p className="text-xs text-muted-foreground">Block #{metadata.creation.block_number?.toLocaleString()}</p>
              )}
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Droplets className="w-3.5 h-3.5 text-blue-500" /> DEX Pairs
              </div>
              <p className="text-2xl font-mono font-bold" data-testid="token-pairs-count">
                {metadata.pairs?.length || 0}
              </p>
              <p className="text-xs text-muted-foreground">across {(new Set((metadata.pairs || []).map(p => p.chain))).size} chain(s)</p>
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <BarChart3 className="w-3.5 h-3.5 text-emerald-500" /> Transfers
              </div>
              <p className="text-2xl font-mono font-bold">
                {(metadata.stats?.transfer_count || 0).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">{(metadata.stats?.holder_count || 0).toLocaleString()} holders</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Token Stats — windowed (1h/6h/12h/24h/7d/30d) */}
      <TokenStats tokenAddress={normalizedAddress} symbol={effectiveSymbol} />

      {/* DEX Pairs list */}
      {metadata?.pairs?.length > 0 && (
        <Card className="border-border/60" data-testid="dex-pairs-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Droplets className="w-5 h-5 text-blue-500" /> DEX Pairs ({metadata.pairs.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground border-b border-border/40">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium">Pair</th>
                    <th className="text-left px-4 py-2.5 font-medium">Pool Address</th>
                    <th className="text-left px-4 py-2.5 font-medium">Chain</th>
                    <th className="text-left px-4 py-2.5 font-medium">Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {metadata.pairs.map((p, i) => (
                    <tr key={`${p.pool_address}-${i}`} className="border-b border-border/30 hover:bg-muted/20" data-testid={`pair-row-${p.pool_address}`}>
                      <td className="px-4 py-2.5 font-medium">{p.pair_label}</td>
                      <td className="px-4 py-2.5 text-xs font-mono"><AddressBadge address={p.pool_address} truncate /></td>
                      <td className="px-4 py-2.5 text-xs">{p.chain}</td>
                      <td className="px-4 py-2.5 text-xs">
                        <div className="flex flex-wrap gap-1">
                          {p.is_krown_pool && <Badge className="bg-blue-500/15 text-blue-400 border border-blue-500/30 text-[10px]">KROWN POOL</Badge>}
                          {p.is_price_reference && <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30 text-[10px]">PRICE REF</Badge>}
                          {!p.is_krown_pool && !p.is_price_reference && <span className="text-muted-foreground text-[10px]">—</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contract creation tile */}
      {metadata?.creation && !metadata.is_native && (
        <Card className="border-border/60" data-testid="creation-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Hammer className="w-5 h-5 text-violet-500" /> Contract Creation
            </CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Creator</p>
              <AddressBadge address={metadata.creation.creator} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Creation Tx</p>
              <TxHashBadge hash={metadata.creation.tx_hash} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Block</p>
              <Link to={`/block/${metadata.creation.block_number}`} className="font-mono text-sm text-blue-400 hover:underline">
                #{metadata.creation.block_number?.toLocaleString()}
              </Link>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Date</p>
              <span className="font-mono text-sm">{new Date(metadata.creation.block_time).toLocaleString()}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ETH Price Info (for KROWN) */}
      {isKrown && ethPrice && (
        <div className="flex items-center gap-2 text-sm p-3 rounded-lg bg-muted/50 border">
          <Info className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">ETH/USD Price for volume calculation:</span>
          {ethPrice.eth_price_usd ? (
            <>
              <span className="font-mono font-medium">${formatNumber(ethPrice.eth_price_usd.toFixed(2))}</span>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="text-xs ml-1">
                    {getPriceSourceLabel(ethPrice.source)}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{ethPrice.source_description}</p>
                </TooltipContent>
              </Tooltip>
            </>
          ) : (
            <span className="text-amber-500 font-medium flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Unavailable - ETH pool volume shows in native ETH
            </span>
          )}
        </div>
      )}

      {/* DEX Trading Volume Panel (KROWN only) */}
      {isKrown && (
        <Card data-testid="dex-volume-panel">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              DEX Trading Volume
              <Badge variant="outline" className="ml-2 text-emerald-600 border-emerald-500/30">
                From Swap Events
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading.dex ? (
              <div className="text-center py-8">Loading DEX metrics...</div>
            ) : (
              <>
                {/* 24H Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <p className="text-sm text-muted-foreground">24H Volume (USD)</p>
                    <p className="text-2xl font-bold text-emerald-600">
                      ${dexMetrics?.dex_metrics_24h?.volume_usd || '0'}
                    </p>
                    {dexMetrics?.dex_metrics_24h?.unpriced_swap_count > 0 && (
                      <p className="text-xs text-amber-500 flex items-center gap-1 mt-1">
                        <AlertTriangle className="w-3 h-3" />
                        Excludes {dexMetrics.dex_metrics_24h.unpriced_swap_count} unpriced
                      </p>
                    )}
                  </div>
                  <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <p className="text-sm text-muted-foreground">24H KROWN Traded</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatNumber(dexMetrics?.dex_metrics_24h?.volume_krown || '0')}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-violet-500/10 border border-violet-500/20">
                    <p className="text-sm text-muted-foreground">24H Swap Transactions</p>
                    <p className="text-2xl font-bold text-violet-600">
                      {dexMetrics?.dex_metrics_24h?.swap_txn_count?.toLocaleString() || '0'}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <p className="text-sm text-muted-foreground">24H Total Swaps</p>
                    <p className="text-2xl font-bold text-amber-600">
                      {dexMetrics?.dex_metrics_24h?.swap_count?.toLocaleString() || '0'}
                    </p>
                  </div>
                </div>

                {/* Pool Breakdown */}
                {dexMetrics?.by_pool && dexMetrics.by_pool.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      Volume by Pool (24H)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {dexMetrics.by_pool.map((pool) => {
                        const isEthPool = pool.token0_symbol === 'ETH' || pool.token1_symbol === 'ETH';
                        const hasUnpriced = pool.unpriced_count > 0;
                        
                        return (
                          <div 
                            key={pool.pool_address}
                            className="p-4 rounded-lg border bg-muted/30"
                            data-testid={`dex-pool-${pool.pool_address}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <Badge className="bg-emerald-500/20 text-emerald-600">
                                {pool.pair || 'Pool'}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                {pool.swap_txn_count} swaps
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <p className="text-muted-foreground">USD Volume</p>
                                {isEthPool && hasUnpriced ? (
                                  <div>
                                    <p className="font-mono font-medium">
                                      ${formatNumber(pool.volume_usd?.toFixed(2) || '0')}
                                    </p>
                                    <Tooltip>
                                      <TooltipTrigger>
                                        <p className="text-xs text-amber-500 flex items-center gap-1">
                                          <AlertTriangle className="w-3 h-3" />
                                          {pool.unpriced_count} unpriced
                                        </p>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>ETH price was unavailable for these swaps</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                ) : (
                                  <p className="font-mono font-medium">
                                    ${formatNumber(pool.volume_usd?.toFixed(2) || '0')}
                                  </p>
                                )}
                              </div>
                              <div>
                                <p className="text-muted-foreground">
                                  {isEthPool ? 'ETH Volume' : 'KROWN Volume'}
                                </p>
                                <p className="font-mono font-medium">
                                  {isEthPool 
                                    ? formatNumber(pool.volume_quote?.toFixed(4) || '0')
                                    : formatNumber(pool.volume_krown?.toFixed(0) || '0')
                                  }
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* On-chain Transfer Volume Panel */}
      <Card data-testid="transfer-volume-panel">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowUpDown className="w-5 h-5 text-blue-500" />
            On-chain Transfer Volume
            <Badge variant="outline" className="ml-2 text-blue-600 border-blue-500/30">
              From Transfer Events
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading.transfer ? (
            <div className="text-center py-8">Loading transfer metrics...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">Window</th>
                    <th className="text-right py-3 px-4 font-medium">Transfer Count</th>
                    <th className="text-right py-3 px-4 font-medium">Unique Txns</th>
                    <th className="text-right py-3 px-4 font-medium">Volume ({tokenInfo.symbol})</th>
                  </tr>
                </thead>
                <tbody>
                  {['1h', '6h', '12h', '24h'].map((window) => {
                    const labels = { '1h': '1 Hour', '6h': '6 Hours', '12h': '12 Hours', '24h': '24 Hours' };
                    const metrics = transferMetrics?.transfer_metrics?.[window] || {};
                    
                    return (
                      <tr key={window} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4 font-medium">{labels[window]}</td>
                        <td className="py-3 px-4 text-right font-mono">{metrics.transfer_count?.toLocaleString() || 0}</td>
                        <td className="py-3 px-4 text-right font-mono">{metrics.txn_count?.toLocaleString() || 0}</td>
                        <td className="py-3 px-4 text-right font-mono">{formatNumber(metrics.volume || '0')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs for Holders and Transfers */}
      <Tabs defaultValue="holders" className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <TabsList>
            <TabsTrigger value="holders" data-testid="holders-tab">
              Top Holders ({(holders.total ?? 0).toLocaleString()})
            </TabsTrigger>
            <TabsTrigger value="transfers" data-testid="transfers-tab">
              Recent Transfers ({(transfers.total ?? 0).toLocaleString()})
            </TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              asChild
              data-testid="export-holders-btn"
            >
              <a href={getExportHoldersUrl(normalizedAddress)} download>
                <Download className="w-4 h-4 mr-2" />
                Export Holders
              </a>
            </Button>
            <Button
              variant="outline"
              size="sm"
              asChild
              data-testid="export-transfers-btn"
            >
              <a href={getExportTransfersUrl(normalizedAddress)} download>
                <Download className="w-4 h-4 mr-2" />
                Export Transfers
              </a>
            </Button>
          </div>
        </div>

        <TabsContent value="holders" className="space-y-4">
          <HoldersTable 
            holders={holders.holders} 
            loading={loading.holders} 
            symbol={tokenInfo.symbol}
          />
          <Pagination
            total={holders.total}
            limit={LIMIT}
            offset={holdersOffset}
            onPageChange={setHoldersOffset}
          />
        </TabsContent>

        <TabsContent value="transfers" className="space-y-4">
          <TransfersTable 
            transfers={transfers.transfers} 
            loading={loading.transfers}
          />
          <Pagination
            total={transfers.total}
            limit={LIMIT}
            offset={transfersOffset}
            onPageChange={setTransfersOffset}
          />
        </TabsContent>
      </Tabs>
    </div>
    </TooltipProvider>
  );
}
